/*
Product Reviews JavaScript

Debugging Instructions:
1. Open browser console
2. Check if window.productReviews exists
3. If it exists, run: window.productReviews.debugPagination()
4. To test pagination: window.productReviews.testPagination()
5. To force reload: window.productReviews.forceReloadAndTest()
6. To change reviews per page: window.productReviews.setReviewsPerPage(5)
7. To force pagination update: window.productReviews.forceUpdatePagination()
8. To debug JSON loading: window.productReviews.debugJsonLoading()
9. To manually set JSON URL: window.productReviews.setJsonUrl('your-url-here')

Common Issues:
- JSON file not loading (check network tab)
- Event listeners not working (check console for click events)
- Pagination not updating (check filteredReviews length)
- Reviews per page too low (use setReviewsPerPage to test)
- Shopify hosted environment JSON path issues (use debugJsonLoading)
*/

class ProductReviews {
  constructor() {
    console.log('ProductReviews constructor called');
    this.reviews = [];
    this.currentPage = 1;
    this.reviewsPerPage = 3;
    this.filteredReviews = [];
    this.productImages = {};
    console.log('ProductReviews constructor completed, calling init');
    console.log('Initial settings - reviewsPerPage:', this.reviewsPerPage, 'currentPage:', this.currentPage);
    this.init();
  }

  // Method to change reviews per page
  setReviewsPerPage(count) {
    console.log('Changing reviews per page from', this.reviewsPerPage, 'to', count);
    this.reviewsPerPage = count;
    this.currentPage = 1;
    this.displayReviews();
    this.updatePagination();
    console.log('Reviews per page updated');
  }

  // Method to manually trigger pagination update
  forceUpdatePagination() {
    console.log('Forcing pagination update...');
    this.updatePagination();
    this.displayReviews();
    console.log('Pagination update forced');
  }

  async init() {
    try {
      console.log('ProductReviews initialization started');
      await this.loadReviews();
      console.log('Reviews loaded, setting up event listeners');
      this.setupEventListeners();
      console.log('Event listeners set up, updating statistics');
      this.updateStatistics();
      console.log('Statistics updated, displaying reviews');
      this.displayReviews();
      console.log('Reviews displayed, updating pagination');
      this.updatePagination();
      console.log('ProductReviews initialization completed');
      
      // Handle product-specific mode
      this.handleProductSpecificMode();
    } catch (error) {
      console.error('Error initializing product reviews:', error);
    }
  }

  async loadReviews() {
    try {
      console.log('Attempting to load reviews from JSON file...');
      console.log('Config available:', !!window.productReviewsConfig);
      console.log('Config JSON URL:', window.productReviewsConfig?.jsonUrl);
      
      // Get the current script URL to determine the base path
      const scripts = document.querySelectorAll('script[src*="product-reviews.js"]');
      let basePath = '';
      if (scripts.length > 0) {
        const scriptSrc = scripts[0].src;
        basePath = scriptSrc.substring(0, scriptSrc.lastIndexOf('/') + 1);
        console.log('Detected base path from script:', basePath);
      }
      
      // Try multiple possible paths for the JSON file
      const possiblePaths = [
        // Use config URL if available (from Liquid template)
        window.productReviewsConfig?.jsonUrl,
        `${basePath}mock-reviews.json`,
        '/assets/mock-reviews.json',
        'mock-reviews.json',
        './mock-reviews.json',
        '../assets/mock-reviews.json'
      ].filter(Boolean); // Remove undefined values
      
      console.log('Possible paths to try:', possiblePaths);
      
      let response = null;
      let successfulPath = null;
      
      for (const path of possiblePaths) {
        try {
          console.log('Trying path:', path);
          response = await fetch(path);
          console.log('Response status:', response.status, 'for path:', path);
          if (response.ok) {
            successfulPath = path;
            console.log('Successfully loaded from path:', path);
            break;
          } else {
            console.log('Failed to load from path:', path, 'Status:', response.status);
          }
        } catch (pathError) {
          console.log('Error loading from path:', path, pathError);
        }
      }
      
      if (!response || !response.ok) {
        throw new Error(`Failed to load JSON from any path. Last status: ${response?.status}`);
      }
      
      const data = await response.json();
      this.reviews = data.reviews || [];
      this.filteredReviews = [...this.reviews];
      console.log('Successfully loaded reviews from JSON:', this.reviews.length);
      console.log('Sample review:', this.reviews[0]);
      
      // Load product images from section settings
      this.loadProductImages();
      
      // Populate product filter dropdown
      this.populateProductFilter();
    } catch (error) {
      console.error('Error loading reviews from JSON:', error);
      console.log('Falling back to default reviews...');
      // Fallback to default reviews if JSON fails to load
      this.reviews = this.getDefaultReviews();
      this.filteredReviews = [...this.reviews];
      console.log('Using fallback reviews:', this.reviews.length);
      console.log('Fallback reviews loaded successfully with', this.reviews.length, 'reviews');
    }
  }

  loadProductImages() {
    // Get product images from section settings
    const section = document.querySelector('.product-reviews-section');
    if (section) {
      const sectionId = section.getAttribute('data-section-id');
      if (sectionId) {
        // Try to get product images from section settings
        // This would need to be passed from Liquid template
        const productImagesData = window.productImagesData || {};
        this.productImages = productImagesData;
        console.log('Product images loaded:', this.productImages);
      }
    }
    
    // Load product-specific image if in product-specific mode
    if (window.productSpecificConfig?.isProductSpecific && window.productSpecificConfig?.productImage) {
      this.productImages[window.productSpecificConfig.productTitle] = window.productSpecificConfig.productImage;
      console.log('Product-specific image loaded:', window.productSpecificConfig.productImage);
    }
  }

  populateProductFilter() {
    const productFilter = document.getElementById('product-name-filter');
    if (!productFilter) return;

    // Get unique product names from reviews
    const productNames = [...new Set(this.reviews.map(review => review.product_name))];
    console.log('Unique product names found:', productNames);

    // Clear existing options except the first one
    const firstOption = productFilter.querySelector('option');
    productFilter.innerHTML = '';
    if (firstOption) {
      productFilter.appendChild(firstOption);
    }

    // Add product name options
    productNames.forEach(productName => {
      const option = document.createElement('option');
      option.value = productName;
      option.textContent = productName;
      productFilter.appendChild(option);
    });

    console.log('Product filter populated with', productNames.length, 'products');
  }

  getDefaultReviews() {
    return [
      {
        rating: 5,
        reviewer_name: 'Janie L.',
        age_range: '60 to 69',
        recommends: true,
        product_name: 'MEGA HEALTH Softgels',
        review_title: 'Time-Line is my gift to be my healthiest self.',
        review_date: '5 months ago',
        review_text: 'I was experiencing low energy, insomnia, and migraines. Since taking this product, I have improved energy, better rest, decreased migraines, and better bone/muscle health and mental rejuvenation.'
      },
      {
        rating: 5,
        reviewer_name: 'Alice T.',
        age_range: 'Over 70',
        recommends: true,
        product_name: 'MEGA HEALTH Softgels',
        review_title: 'MEGA HEALTH soft gels Rock!!!',
        review_date: '5 months ago',
        review_text: 'MEGA HEALTH soft gels are an important supplement for me (1000 mg daily). I\'ve noticed improvements in strength, muscle maintenance, and skin appearance/feel. I don\'t want to ever be without MEGA HEALTH!!!!'
      },
      {
        rating: 5,
        reviewer_name: 'Alicia H.',
        age_range: '50 to 59',
        recommends: true,
        product_name: 'MEGA HEALTH Softgels',
        review_title: 'Timeline for Mitochondrial Health & Energy Optimization',
        review_date: '8 months ago',
        review_text: 'As a 53-year-old National Board Certified Functional Medicine Health Coach, I was searching for evidence-based tools for mitochondrial function and vitality. Timeline has been transformative for mitochondrial biogenesis.'
      }
    ];
  }

  setupEventListeners() {
    // Filter event listeners
    const productNameFilter = document.getElementById('product-name-filter');
    const allRatings = document.getElementById('all-ratings');

    if (productNameFilter) {
      productNameFilter.addEventListener('change', () => this.applyFilters());
    }
    if (allRatings) {
      allRatings.addEventListener('change', () => this.applyFilters());
    }

    // Pagination event listeners - using event delegation for dynamic elements
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!target) return;
      
      console.log('Click event detected on:', target);
      
      // Previous button
      if (target.closest && target.closest('.pagination-arrow.prev')) {
        console.log('Previous button clicked');
        e.preventDefault();
        this.previousPage();
      }
      
      // Next button
      if (target.closest && target.closest('.pagination-arrow.next')) {
        console.log('Next button clicked');
        e.preventDefault();
        this.nextPage();
      }
      
      // Page numbers
      if (target.classList && target.classList.contains('page-number')) {
        console.log('Page number clicked:', target.textContent);
        e.preventDefault();
        const page = parseInt(target.textContent || '');
        if (!isNaN(page)) {
          this.goToPage(page);
        }
      }
    });
  }

  applyFilters() {
    const productNameFilter = document.getElementById('product-name-filter')?.value;
    const allRatings = document.getElementById('all-ratings')?.value;

    console.log('Applying filters:', { productNameFilter, allRatings });

    this.filteredReviews = this.reviews.filter(review => {
      let matches = true;

      // Rating filter
      if (allRatings && allRatings !== '') {
        matches = matches && review.rating === parseInt(allRatings);
      }

      // Product name filter
      if (productNameFilter && productNameFilter !== '') {
        matches = matches && review.product_name === productNameFilter;
      }

      return matches;
    });

    console.log('Filtered reviews:', this.filteredReviews.length);
    this.currentPage = 1;
    this.updateStatistics();
    this.displayReviews();
    this.updatePagination();
  }

  handleProductSpecificMode() {
    if (window.productSpecificConfig?.isProductSpecific && window.productSpecificConfig?.productTitle) {
      console.log('Product-specific mode enabled for:', window.productSpecificConfig.productTitle);
      
      // Filter reviews to show only the specific product
      this.filteredReviews = this.reviews.filter(review => 
        review.product_name === window.productSpecificConfig.productTitle
      );
      
      console.log('Filtered to product-specific reviews:', this.filteredReviews.length);
      
      // Update statistics based on filtered reviews
      this.updateStatistics();
      this.displayReviews();
      this.updatePagination();
    }
  }



  displayReviews() {
    const reviewsList = document.querySelector('.reviews-list');
    if (!reviewsList) {
      console.error('Reviews list element not found!');
      return;
    }

    const startIndex = (this.currentPage - 1) * this.reviewsPerPage;
    const endIndex = startIndex + this.reviewsPerPage;
    const pageReviews = this.filteredReviews.slice(startIndex, endIndex);

    console.log('Displaying reviews:', {
      startIndex,
      endIndex,
      pageReviews: pageReviews.length,
      totalFiltered: this.filteredReviews.length,
      currentPage: this.currentPage,
      reviewsPerPage: this.reviewsPerPage,
      pageReviewsNames: pageReviews.map(r => r.reviewer_name)
    });

    reviewsList.innerHTML = pageReviews.map(review => this.createReviewHTML(review)).join('');
  }

  createReviewHTML(review) {
    const stars = this.createStarsHTML(review.rating);
    
    // Truncate review text to 50 characters only on small screens
    const fullText = review.review_text;
    const truncatedText = fullText.length > 50 ? fullText.substring(0, 50) + '...' : fullText;
    
    return `
      <div class="review-item">
        <div class="review-header">
          <div class="review-stars">
            ${stars}
          </div>
          
          <div class="reviewer-info">
            <div class="reviewer-name">${review.reviewer_name}</div>
            <div class="reviewer-badge">
              <span class="verified-buyer">Verified buyer</span>
              ${review.recommends ? '<span class="recommends">Recommends this product</span>' : ''}
            </div>
            <div class="reviewer-age">${review.age_range}</div>
          </div>
        </div>

        <div class="review-product">
          <span class="product-name">${review.product_name}</span>
        </div>

        <div class="review-content">
          <h3 class="review-title">${review.review_title}</h3>
          <div class="review-date">${review.review_date}</div>
          <div class="review-text">
            <span class="review-text-full">${fullText}</span>
            <span class="review-text-short">${truncatedText}</span>
          </div>
        </div>
      </div>
    `;
  }

  getProductImage(productName) {
    // Return the product image URL if available
    return this.productImages[productName] || null;
  }

  createStarsHTML(rating) {
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars += '<span class="star filled">★</span>';
      } else {
        stars += '<span class="star empty">☆</span>';
      }
    }
    return stars;
  }

  previousPage() {
    console.log('previousPage called, current page:', this.currentPage);
    if (this.currentPage > 1) {
      this.currentPage--;
      console.log('Moving to previous page:', this.currentPage);
      this.displayReviews();
      this.updatePagination();
      this.scrollToTop();
    } else {
      console.log('Already on first page');
    }
  }

  nextPage() {
    const maxPages = Math.ceil(this.filteredReviews.length / this.reviewsPerPage);
    console.log('nextPage called, current page:', this.currentPage, 'max pages:', maxPages);
    if (this.currentPage < maxPages) {
      this.currentPage++;
      console.log('Moving to next page:', this.currentPage);
      this.displayReviews();
      this.updatePagination();
      this.scrollToTop();
    } else {
      console.log('Already on last page');
    }
  }

  goToPage(page) {
    const maxPages = Math.ceil(this.filteredReviews.length / this.reviewsPerPage);
    console.log('goToPage called, target page:', page, 'current page:', this.currentPage, 'max pages:', maxPages);
    if (page >= 1 && page <= maxPages) {
      this.currentPage = page;
      console.log('Moving to page:', this.currentPage);
      this.displayReviews();
      this.updatePagination();
      this.scrollToTop();
    } else {
      console.log('Invalid page number:', page);
    }
  }

  // Scroll to top of the page with smooth animation
  scrollToTop() {
    // Find the product reviews section to scroll to
    const reviewsSection = document.querySelector('.product-reviews-section');
    if (reviewsSection) {
      // Scroll to the section with smooth animation
      reviewsSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    } else {
      // Fallback to scrolling to top of page
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
    }
  }

  updateStatistics() {
    // Use filtered reviews for statistics in product-specific mode
    const reviewsForStats = window.productSpecificConfig?.isProductSpecific ? this.filteredReviews : this.reviews;
    
    // Calculate average rating
    const totalRating = reviewsForStats.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = reviewsForStats.length > 0 ? (totalRating / reviewsForStats.length).toFixed(1) : '0.0';
    
    // Update average rating display
    const averageRatingElement = document.getElementById('average-rating');
    if (averageRatingElement) {
      averageRatingElement.textContent = averageRating;
    }
    
    // Update total reviews count
    const totalReviewsElement = document.getElementById('total-reviews-count');
    if (totalReviewsElement) {
      totalReviewsElement.textContent = reviewsForStats.length;
    }
    
    // Calculate star distribution
    const starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviewsForStats.forEach(review => {
      starCounts[review.rating]++;
    });
    
    // Update star distribution display
    const starDistributionElement = document.getElementById('star-distribution');
    if (starDistributionElement) {
      let distributionHTML = '';
      
      for (let i = 5; i >= 1; i--) {
        const count = starCounts[i];
        const percentage = reviewsForStats.length > 0 ? Math.round((count / reviewsForStats.length) * 100) : 0;
        
        distributionHTML += `
          <div class="star-row">
            <span class="star-label">${i} stars</span>
            <div class="star-bar">
              <div class="star-bar-fill" style="width: ${percentage}%"></div>
            </div>
            <span class="star-percentage">${percentage}%</span>
          </div>
        `;
      }
      
      starDistributionElement.innerHTML = distributionHTML;
    }
    
    console.log('Statistics updated:', { 
      averageRating, 
      totalReviews: reviewsForStats.length, 
      starCounts,
      isProductSpecific: window.productSpecificConfig?.isProductSpecific
    });
  }

  updatePagination() {
    const maxPages = Math.ceil(this.filteredReviews.length / this.reviewsPerPage);
    const paginationNumbers = document.querySelector('.pagination-numbers');
    
    if (!paginationNumbers) {
      console.error('Pagination numbers element not found!');
      return;
    }

    console.log('Updating pagination:', { 
      totalReviews: this.filteredReviews.length, 
      reviewsPerPage: this.reviewsPerPage, 
      maxPages, 
      currentPage: this.currentPage,
      filteredReviews: this.filteredReviews.map(r => r.reviewer_name)
    });

    let paginationHTML = '';
    
    if (maxPages <= 1) {
      // Only one page, show just page 1
      paginationHTML = `<span class="page-number active">1</span>`;
      console.log('Only one page detected, showing single page');
    } else if (maxPages <= 5) {
      // Show all pages
      for (let i = 1; i <= maxPages; i++) {
        const isActive = i === this.currentPage;
        paginationHTML += `<span class="page-number ${isActive ? 'active' : ''}">${i}</span>`;
      }
      console.log(`Showing all ${maxPages} pages`);
    } else {
      // Show first page, current page, and last page with ellipsis
      paginationHTML += `<span class="page-number ${this.currentPage === 1 ? 'active' : ''}">1</span>`;
      
      if (this.currentPage > 3) {
        paginationHTML += `<span class="page-ellipsis">...</span>`;
      }
      
      for (let i = Math.max(2, this.currentPage - 1); i <= Math.min(maxPages - 1, this.currentPage + 1); i++) {
        if (i !== 1 && i !== maxPages) {
          const isActive = i === this.currentPage;
          paginationHTML += `<span class="page-number ${isActive ? 'active' : ''}">${i}</span>`;
        }
      }
      
      if (this.currentPage < maxPages - 2) {
        paginationHTML += `<span class="page-ellipsis">...</span>`;
      }
      
      if (maxPages > 1) {
        paginationHTML += `<span class="page-number ${this.currentPage === maxPages ? 'active' : ''}">${maxPages}</span>`;
      }
      console.log(`Showing pagination with ellipsis for ${maxPages} pages`);
    }

    paginationNumbers.innerHTML = paginationHTML;

    // Update arrow states
    const prevButton = document.querySelector('.pagination-arrow.prev');
    const nextButton = document.querySelector('.pagination-arrow.next');
    
    if (prevButton) {
      prevButton.disabled = this.currentPage === 1;
      prevButton.style.opacity = this.currentPage === 1 ? '0.5' : '1';
      prevButton.style.cursor = this.currentPage === 1 ? 'not-allowed' : 'pointer';
    }
    
    if (nextButton) {
      nextButton.disabled = this.currentPage === maxPages;
      nextButton.style.opacity = this.currentPage === maxPages ? '0.5' : '1';
      nextButton.style.cursor = this.currentPage === maxPages ? 'not-allowed' : 'pointer';
    }

    console.log('Pagination updated:', { maxPages, currentPage: this.currentPage });
  }

  // Debug method for testing pagination
  debugPagination() {
    console.log('=== PAGINATION DEBUG ===');
    console.log('Total reviews:', this.reviews.length);
    console.log('Filtered reviews:', this.filteredReviews.length);
    console.log('Reviews per page:', this.reviewsPerPage);
    console.log('Current page:', this.currentPage);
    console.log('Max pages:', Math.ceil(this.filteredReviews.length / this.reviewsPerPage));
    console.log('Review names:', this.filteredReviews.map(r => r.reviewer_name));
    console.log('Pagination element exists:', !!document.querySelector('.pagination-numbers'));
    console.log('Pagination HTML:', document.querySelector('.pagination-numbers')?.innerHTML);
    console.log('=======================');
  }

  // Test method for manually testing pagination
  testPagination() {
    console.log('=== TESTING PAGINATION ===');
    this.debugPagination();
    
    const maxPages = Math.ceil(this.filteredReviews.length / this.reviewsPerPage);
    console.log('Testing navigation to page 2...');
    if (maxPages >= 2) {
      this.goToPage(2);
      console.log('After going to page 2:');
      this.debugPagination();
      
      console.log('Testing navigation back to page 1...');
      this.goToPage(1);
      console.log('After going back to page 1:');
      this.debugPagination();
    } else {
      console.log('Not enough pages to test navigation');
    }
    console.log('=== PAGINATION TEST COMPLETE ===');
  }

  // Force reload and test pagination
  async forceReloadAndTest() {
    console.log('=== FORCE RELOAD AND TEST ===');
    this.currentPage = 1;
    await this.loadReviews();
    this.filteredReviews = [...this.reviews];
    this.updateStatistics();
    this.displayReviews();
    this.updatePagination();
    this.testPagination();
    console.log('=== FORCE RELOAD COMPLETE ===');
  }

  // Debug method to test JSON loading specifically
  async debugJsonLoading() {
    console.log('=== JSON LOADING DEBUG ===');
    console.log('Config object:', window.productReviewsConfig);
    console.log('Script elements:', document.querySelectorAll('script[src*="product-reviews.js"]'));
    
    const scripts = document.querySelectorAll('script[src*="product-reviews.js"]');
    if (scripts.length > 0) {
      console.log('Script src:', scripts[0].src);
    }
    
    // Test fetch directly
    const testUrls = [
      window.productReviewsConfig?.jsonUrl,
      '/assets/mock-reviews.json',
      'mock-reviews.json'
    ].filter(Boolean);
    
    for (const url of testUrls) {
      try {
        console.log('Testing fetch for:', url);
        const response = await fetch(url);
        console.log('Response for', url, ':', response.status, response.statusText);
        if (response.ok) {
          const data = await response.json();
          console.log('Successfully loaded data from', url, ':', data);
        }
      } catch (error) {
        console.log('Error testing', url, ':', error);
      }
    }
    console.log('=== JSON LOADING DEBUG COMPLETE ===');
  }

  // Method to manually set JSON URL for debugging
  setJsonUrl(url) {
    console.log('Manually setting JSON URL to:', url);
    if (!window.productReviewsConfig) {
      window.productReviewsConfig = {};
    }
    window.productReviewsConfig.jsonUrl = url;
    console.log('JSON URL set, you can now call forceReloadAndTest() to reload');
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM Content Loaded event fired');
  const productReviewsSection = document.querySelector('.product-reviews-section');
  console.log('Product reviews section found:', !!productReviewsSection);
  if (productReviewsSection) {
    console.log('Initializing ProductReviews class');
    // Wait a bit for config to be available
    setTimeout(() => {
      window.productReviews = new ProductReviews();
      console.log('ProductReviews instance available as window.productReviews');
    }, 100);
  } else {
    console.log('Product reviews section not found, skipping initialization');
  }
});

// Fallback initialization for cases where DOMContentLoaded has already fired
if (document.readyState === 'loading') {
  console.log('Document still loading, waiting for DOMContentLoaded');
} else {
  console.log('Document already loaded, initializing immediately');
  const productReviewsSection = document.querySelector('.product-reviews-section');
  console.log('Product reviews section found (immediate):', !!productReviewsSection);
  if (productReviewsSection) {
    console.log('Initializing ProductReviews class (immediate)');
    // Wait a bit for config to be available
    setTimeout(() => {
      window.productReviews = new ProductReviews();
      console.log('ProductReviews instance available as window.productReviews');
    }, 100);
  } else {
    console.log('Product reviews section not found (immediate), skipping initialization');
  }
} 